import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import { useEffect, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { useSelector } from 'react-redux';
import { resolvePath, useNavigate, useQuery } from '../../../routes';
import { Order, PaymentMethodType } from '../../../models';
import { fromAccountUsers } from '../../../store';
import { BUSINESS_EMAIL } from '../../../utils/utils';
import { order, plan } from '../../../layout/routes';
import { CompletePayment, formatCurrency } from '../../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../utils/Constants';

const useStyles = makeStyles((theme => ({
  rootContainer: {
    backgroundColor: '#fef9e7',
    padding: '8px 24px',
    borderRadius: theme.spacing(2),
    width: '100%',
  },
  orderNumberContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: -10,
  },
  orderNumber: {
    fontSize: 20,
    fontWeight: 600,
    color: '#22283c',
    lineHeight: 1.5,
    marginBottom: 5,
  },
  orderStatus: {
    height: 27,
    margin: 0,
    marginLeft: 20,
    alignItems: 'center',
    padding: '0 8px',
    paddingTop: 3,
    borderRadius: 4,
    backgroundColor: '#f0be0d',
    color: '#22283c',
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: -10,
  },
  orderDetailContainer: {
    margin: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  orderDetailTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: '#808b9a',
  },
  orderDetailValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#233145',
  },
  description: {
    fontSize: 14,
    lineHeight: 1.5,
    color: '#22283c',
  },
  card: {
    display: 'flex',
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
    margin: theme.spacing(0, 0, 0),
    padding: theme.spacing(3, 0, 1, 2),
  },
  cardTitleContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: theme.spacing(3),
    padding: theme.spacing(0, 1, 1, 0),
  },
  dialogContent: {
    lineHeight: 1.5,
    fontSize: theme.spacing(2),
    padding: theme.spacing(0, 0, 3, 2),
  },
  businessEmail: {
    textDecoration: 'none',
    color: '#598ef9',
  },
  creditCardNoteContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    marginTop: 10,
  },
  noteIcon: {
    width: 24,
    height: 24,
    backgroundColor: '#FF6B00',
    borderRadius: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    flexShrink: 0,
    marginRight: 10,
  },
  divider: {
    width: 1,
    height: 40,
    backgroundColor: '#E5E7EB',
    marginLeft: 20,
    marginRight: 20,
  },
  actionBtnContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    marginLeft: 'auto',
    marginRight: 0,
    marginTop: 10,
  },
})));

interface OrderDetailSectionProps {
  pendingOrder: Order,
}

const OrderDetailSection = (props: OrderDetailSectionProps) => {
  const classes = useStyles();
  const { get, queryDictionary } = useQuery();
  const { navigate, replaceQuery } = useNavigate();
  const [showInstructionPopUp, setShowInstructionPopUp] = useState(
    (get('showInstructionPopup') ?? 'false') === 'true',
  );
  const {
    pendingOrder,
  } = props;

  const accountUser = useSelector(
    fromAccountUsers.findAccountUserByAccountId(pendingOrder.accountId),
  );

  const intl = useIntl();
  const isCreditCardOrder = !!pendingOrder.stripeInvoiceUrl
    && pendingOrder.paymentMethodType === PaymentMethodType.CREDIT_CARD;

  const businessEmailAnchor = () => (
    <a id="order-confirmation-dialog-anchor" className={classes.businessEmail} href={BUSINESS_EMAIL}>
      contact us
    </a>
  );

  const renderInstructionPopUp = () => (
    <Dialog
      open={showInstructionPopUp}
      id="order-confirmation-dialog"
      onClose={handleCloseInstructionDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          paddingX: 2,
          borderRadius: 1.5,
        },
      }}
    >
      <div className={classes.card}>
        <div id="order-confirmation-dialog-title" className={classes.cardTitleContent}>
          <DialogTitle className={classes.title}>
            <FormattedMessage id="page.expand.plan.order.confirm.dialog.title" />
          </DialogTitle>
          <IconButton
            id="order-confirmation-dialog-closed"
            aria-label="close"
            onClick={handleCloseInstructionDialog}
            edge="end"
            size="small"
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </div>
      </div>
      <DialogContent id="order-confirmation-dialog-content" className={classes.dialogContent}>
        {intl.formatMessage(
          { id: 'page.expand.plan.order.confirm.dialog.body.instruction_first' },
          {
            br: <br />,
          },
        )}
        {intl.formatMessage(
          { id: 'page.expand.plan.order.confirm.dialog.body.instruction_second' },
          {
            email: accountUser?.account.ownerEmail,
          },
        )}
        {intl.formatMessage(
          { id: 'page.expand.plan.order.confirm.dialog.body.instruction_third' },
          {
            a: businessEmailAnchor,
            br: <br />,
          },
        )}
      </DialogContent>
    </Dialog>
  );

  useEffect(() => {
    if (showInstructionPopUp) {
      window.history.replaceState(
        null,
        '',
        resolvePath(plan, undefined, {
          accountId: pendingOrder.accountId,
        }),
      );
    }
  }, [showInstructionPopUp]);

  const handleCloseInstructionDialog = () => {
    setShowInstructionPopUp(false);
  };

  const renderDetailItem = (id: string, title: string, description: string) => (
    <Grid
      id={id}
      className={classes.orderDetailContainer}
      container
    >
      <Grid
        className={classes.orderDetailTitle}
        xs={1.5}
      >
        <p style={{ margin: '5px 0px 0px' }}>
          <FormattedMessage
            id={title}
          />
        </p>
      </Grid>
      <Grid
        className={classes.orderDetailValue}
        xs={2.5}
      >
        <p style={{ margin: '5px 0px 0px' }}>{description}</p>
      </Grid>
    </Grid>
  );

  const getDueDate = () => intl.formatDate(new Date(pendingOrder?.dueDate ?? ''), {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const viewOrderDetail = () => {
    navigate(order.path, replaceQuery(queryDictionary()));
  };

  const renderNoteForBankTransfer = () => (
    <p className={classes.description}>
      <FormattedMessage
        id="pending.order.note.bank_transfer"
      />
    </p>
  );

  const renderNoteForCreditCard = () => (
    <div className={classes.creditCardNoteContainer}>
      <div className={classes.noteIcon}>!</div>
      <div className={classes.description}>
        <FormattedMessage
          id="pending.order.note.credit_card"
        />
      </div>
    </div>
  );

  const renderActionBtns = () => (
    <div className={classes.actionBtnContainer}>
      <CompletePayment
        order={pendingOrder}
      />
      <div className={classes.divider} />
      <Button
        id="checkout-btn"
        variant="contained"
        onClick={viewOrderDetail}
        sx={theme => ({
          height: theme.spacing(5),
          color: '#5850EC',
          fontSize: 14,
          fontWeight: 500,
          padding: '8px 16p',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        })}
      >
        <FormattedMessage
          id="pending.order.view.order"
        />
      </Button>
    </div>
  );

  return (
    <>
      <div id="order-detail-section" className={classes.rootContainer}>
        <div id="pending-order-number" className={classes.orderNumberContainer}>
          <p className={classes.orderNumber}>
            <FormattedMessage
              id="pending.order.number"
              values={{
                number: pendingOrder?.id,
              }}
            />
          </p>
          <p className={classes.orderStatus}>Waiting for Payment</p>
          {isCreditCardOrder && renderActionBtns()}
        </div>
        {renderDetailItem(
          'pending-order-amount',
          'pending.order.amount_due',
          formatCurrency(intl, pendingOrder.totalAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS),
        )}
        {renderDetailItem('pending-order-due-date', 'pending.order.due_date', getDueDate())}
        {isCreditCardOrder ? renderNoteForCreditCard() : renderNoteForBankTransfer()}
      </div>
      {renderInstructionPopUp()}
    </>
  );
};

export default OrderDetailSection;
